// Time zone helpers (no date library in the domain package). A gig's times
// are shown and edited in its main venue's IANA time zone (Gig.timeZone), a
// call's in its own venue's (Call.timeZone) — never the viewer's browser
// zone, which would silently shift a Chicago show for someone in New York.

export interface WallTime {
  y: number;
  m: number; // 1-12
  d: number;
  h: number;
  mi: number;
  s: number;
  ms: number;
}

export function wallTimeIn(date: Date, timeZone: string): WallTime {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  }).formatToParts(date);
  const get = (t: string) => Number(parts.find(p => p.type === t)?.value);
  return {
    y: get('year'),
    m: get('month'),
    d: get('day'),
    h: get('hour'),
    mi: get('minute'),
    s: get('second'),
    ms: ((date.getTime() % 1000) + 1000) % 1000,
  };
}

// Offset (ms) of `timeZone` from UTC at instant `ts`.
function zoneOffsetMs(ts: number, timeZone: string): number {
  const w = wallTimeIn(new Date(ts), timeZone);
  return Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s) - Math.floor(ts / 1000) * 1000;
}

// The instant whose wall-clock time in `timeZone` is the given fields. Days
// overflow naturally (d = 32 rolls into the next month). Re-checks the offset
// once so a result landing across a DST boundary still gets the right one.
export function instantFromWallTime(w: WallTime, timeZone: string): Date {
  const asUtc = Date.UTC(w.y, w.m - 1, w.d, w.h, w.mi, w.s, w.ms);
  let ts = asUtc - zoneOffsetMs(asUtc, timeZone);
  const corrected = asUtc - zoneOffsetMs(ts, timeZone);
  if (corrected !== ts) ts = corrected;
  return new Date(ts);
}

// Moves an instant forward (or back) by whole calendar days in `timeZone`,
// preserving its wall-clock time — a 7pm call stays 7pm across a DST change,
// which adding n * 24h would not.
export function shiftDays(date: Date, days: number, timeZone: string): Date {
  const w = wallTimeIn(date, timeZone);
  return instantFromWallTime({ ...w, d: w.d + days }, timeZone);
}

// Midnight at the start of a YYYY-MM-DD calendar day in `timeZone`.
export function startOfDayIn(ymd: string, timeZone: string): Date {
  const [y, m, d] = ymd.split('-').map(Number);
  return instantFromWallTime({ y, m, d, h: 0, mi: 0, s: 0, ms: 0 }, timeZone);
}

export function calendarDayNumber(w: { y: number; m: number; d: number }): number {
  return Date.UTC(w.y, w.m - 1, w.d) / 86_400_000;
}

const pad = (n: number) => String(n).padStart(2, '0');

// "YYYY-MM-DD" of the instant's calendar day in `timeZone`.
export function zonedDateStr(date: Date, timeZone: string): string {
  const w = wallTimeIn(date, timeZone);
  return `${w.y}-${pad(w.m)}-${pad(w.d)}`;
}

// "YYYY-MM-DDTHH:mm" (the <input type="datetime-local"> format) of the
// instant's wall-clock time in `timeZone`.
export function toZonedInput(date: Date, timeZone: string): string {
  const w = wallTimeIn(date, timeZone);
  return `${w.y}-${pad(w.m)}-${pad(w.d)}T${pad(w.h)}:${pad(w.mi)}`;
}

// Inverse of toZonedInput: the instant for a datetime-local string read as
// wall-clock time in `timeZone`.
export function fromZonedInput(value: string, timeZone: string): Date {
  const [datePart, timePart = '00:00'] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [h, mi] = timePart.split(':').map(Number);
  return instantFromWallTime({ y, m, d, h, mi, s: 0, ms: 0 }, timeZone);
}

export function formatZonedDate(date: Date, timeZone: string, withWeekday = false): string {
  return date.toLocaleDateString('en-US', {
    timeZone,
    ...(withWeekday ? { weekday: 'short' as const } : {}),
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatZonedTime(date: Date, timeZone: string, withZoneName = false): string {
  return date.toLocaleTimeString('en-US', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    ...(withZoneName ? { timeZoneName: 'short' as const } : {}),
  });
}

export function isValidTimeZone(tz: string | undefined | null): tz is string {
  if (!tz) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

// Every IANA zone the runtime knows (for pickers), falling back to just
// `fallback` where Intl.supportedValuesOf isn't available.
export function listTimeZones(fallback: string): string[] {
  try {
    return (Intl as unknown as { supportedValuesOf: (k: string) => string[] }).supportedValuesOf('timeZone');
  } catch {
    return [fallback];
  }
}

// ---- the zone costing groups days in ------------------------------------------
//
// Cost depends on WHICH CALENDAR DAY a call falls on (a day rate is one charge
// per day; the short-rest and holiday checks work per day), so the zone days
// are grouped in has to be the one the times are SHOWN in — otherwise an
// evening call "moves" to the next day and a $400 day becomes $800. Times are
// shown in the gig's main venue's zone, so costing uses that too: the venue's
// zone, then the organization's, then UTC as a last resort (reported via
// `source` so a screen can warn that it's a guess, not a setting).
export interface CostZone {
  zone: string;
  source: 'venue' | 'org' | 'default';
}

export function resolveCostZone(venueZone: string | undefined | null, orgZone: string | undefined | null): CostZone {
  if (isValidTimeZone(venueZone)) return { zone: venueZone, source: 'venue' };
  if (isValidTimeZone(orgZone)) return { zone: orgZone, source: 'org' };
  return { zone: 'UTC', source: 'default' };
}
